import { FC, useState, useMemo } from "react";
import { Table, Tag, Button, Input, Collapse } from "antd";
import type { CollapseProps } from "antd";
import {
  Briefcase,
  Search,
  ExternalLink,
  Calendar,
  Users,
  MapPin,
  Package
} from "lucide-react";
import { Link } from "react-router-dom";
import { useGetBookings } from "~/services/booking";
import { useMissionControlStore } from "../../mission-control.store";
import { capitalize, formatDateTime } from "~/utils";
import dayjs from "dayjs";

type Props = {};

export const BookingsServicesView: FC<Props> = () => {
  const { selectedProjectId } = useMissionControlStore();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: bookingsData, isLoading } = useGetBookings(
    selectedProjectId
      ? {
          projectId: selectedProjectId,
          pageSize: 500,
        }
      : undefined
  );

  const filteredBookings = useMemo(() => {
    if (!bookingsData?.items) return [];

    if (!searchTerm) return bookingsData.items;

    return bookingsData.items.filter((booking) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        booking.referenceNumber?.toLowerCase().includes(searchLower) ||
        booking.client?.companyName?.toLowerCase().includes(searchLower) ||
        booking.client?.firstName?.toLowerCase().includes(searchLower) ||
        booking.client?.lastName?.toLowerCase().includes(searchLower)
      );
    });
  }, [bookingsData, searchTerm]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      completed: "success",
      "in-progress": "processing",
      scheduled: "blue",
      draft: "default",
      cancelled: "error",
    };
    return statusMap[status?.toLowerCase()] || "default";
  };

  const renderServiceTickets = (booking: any) => {
    if (!booking.serviceTicket || booking.serviceTicket.length === 0) {
      return <p className="text-gray-400 text-sm">No service tickets</p>;
    }

    const serviceColumns = [
      {
        title: "Serial",
        dataIndex: "serial",
        key: "serial",
        width: 150,
      },
      {
        title: "Type",
        dataIndex: "serviceType",
        key: "serviceType",
        width: 150,
        render: (type: string) => (
          <Tag color="blue">{capitalize(type)}</Tag>
        ),
      },
      {
        title: "Date/Time",
        dataIndex: "serviceDatetime",
        key: "serviceDatetime",
        width: 200,
        render: (datetime: string) => (
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-gray-400" />
            <span className="text-sm">{formatDateTime(dayjs(datetime))}</span>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status: string) => (
          <Tag color={getStatusColor(status)}>
            {capitalize(status || "pending")}
          </Tag>
        ),
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        width: 200,
        render: (location: any) => {
          if (!location) return <span className="text-gray-400">-</span>;

          const locationParts = [
            location.city,
            location.state,
            location.zip
          ].filter(Boolean);

          return locationParts.length > 0 ? (
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-gray-400" />
              <span className="text-sm truncate">{locationParts.join(', ')}</span>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
      },
      {
        title: "Staff",
        dataIndex: "staffs",
        key: "staffs",
        width: 150,
        render: (staffs: any[]) => (
          staffs && staffs.length > 0 ? (
            <div className="flex items-center gap-2">
              <Users size={14} className="text-gray-400" />
              <span className="text-sm">{staffs.length} assigned</span>
            </div>
          ) : (
            <span className="text-gray-400">No staff</span>
          )
        ),
      },
      {
        title: "Inventory",
        dataIndex: "inventories",
        key: "inventories",
        width: 120,
        render: (inventories: any[]) => (
          inventories && inventories.length > 0 ? (
            <div className="flex items-center gap-2">
              <Package size={14} className="text-gray-400" />
              <span className="text-sm">{inventories.length} items</span>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )
        ),
      },
    ];

    return (
      <Table
        dataSource={booking.serviceTicket}
        columns={serviceColumns}
        pagination={false}
        size="small"
        rowKey="id"
        className="mt-2"
      />
    );
  };

  const bookingColumns = [
    {
      title: "Booking ID",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      width: 150,
      render: (ref: string, record: any) => (
        <Link to={`/bookings/${record.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
          {ref}
        </Link>
      ),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      width: 200,
      render: (client: any) => (
        <div>
          <p className="font-medium">
            {client?.companyName || `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "Unknown"}
          </p>
        </div>
      ),
    },
    {
      title: "Service Tickets",
      dataIndex: "serviceTicket",
      key: "serviceTicketCount",
      width: 120,
      render: (tickets: any[]) => (
        <Tag color="purple">{tickets?.length || 0} tickets</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {capitalize(status || "pending")}
        </Tag>
      ),
    },
    {
      title: "Total",
      dataIndex: "invoiceConfig",
      key: "total",
      width: 120,
      render: (config: any) => (
        config?.grandTotal ? (
          <span className="font-medium">${config.grandTotal.toFixed(2)}</span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <Link to={`/bookings/${record.id}`}>
          <Button type="link" size="small" icon={<ExternalLink size={14} />}>
            View
          </Button>
        </Link>
      ),
    },
  ];

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="text-blue-600" size={24} />
          <div>
            <h2 className="text-xl font-semibold">Bookings & Service Tickets</h2>
            <p className="text-sm text-gray-500">
              {filteredBookings.length} booking{filteredBookings.length !== 1 ? "s" : ""} in this project
            </p>
          </div>
        </div>

        <Input
          prefix={<Search size={16} />}
          placeholder="Search bookings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-64"
        />
      </div>

      <div className="bg-white rounded-lg border">
        <Collapse
          accordion
          className="border-none"
          expandIconPosition="end"
          items={filteredBookings.map((booking) => ({
            key: booking.id,
            label: (
              <div className="flex items-center justify-between pr-4">
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-blue-600">{booking.referenceNumber}</span>
                  <span className="text-gray-600">
                    {booking.client?.companyName ||
                     `${booking.client?.firstName || ""} ${booking.client?.lastName || ""}`.trim() ||
                     "Unknown Client"}
                  </span>
                  <Tag color="purple">
                    {booking.serviceTicket?.length || 0} service ticket{booking.serviceTicket?.length !== 1 ? "s" : ""}
                  </Tag>
                  <Tag color={getStatusColor(booking.status)}>
                    {capitalize(booking.status || "pending")}
                  </Tag>
                </div>
                <Link to={`/bookings/${booking.id}`} onClick={(e) => e.stopPropagation()}>
                  <Button type="link" size="small" icon={<ExternalLink size={14} />}>
                    Open Booking
                  </Button>
                </Link>
              </div>
            ),
            children: renderServiceTickets(booking),
          }))}
        />

        {filteredBookings.length === 0 && !isLoading && (
          <div className="p-12 text-center">
            <Briefcase className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-600 mb-2">
              {searchTerm ? "No matching bookings" : "No bookings found"}
            </h3>
            <p className="text-gray-500">
              {searchTerm
                ? "Try adjusting your search terms"
                : "Create bookings and assign them to this project to see them here"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
